'use client'

;!function(){try{var e="undefined"!=typeof globalThis?globalThis:"undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import type React from 'react'
import { Button } from './button'
import { Link } from './link'

export function Dropdown(props: Headless.MenuProps) {
  return <Headless.Menu {...props} />
}

export function DropdownButton<T extends React.ElementType = typeof Button>({
  as = Button,
  ...props
}: { className?: string } & Omit<Headless.MenuButtonProps<T>, 'className'>) {
  return <Headless.MenuButton as={as} {...props} className={clsx('cursor-pointer', props.className)} data-sentry-element="Headless.MenuButton" data-sentry-component="DropdownButton" data-sentry-source-file="dropdown.tsx" />
}

/**
 * `unmount` defaults to `false` so the items are in the server-rendered HTML, hidden, rather than
 * created on first open. Headless UI's own default unmounts a closed menu, which left the links
 * inside it — the product pages in the navbar, the other language in the footer — absent from the
 * document a crawler reads without clicking. Measured on 2026-09-30 by diffing every sitemap URL's
 * no-JS HTML against the rendered page with each menu opened: those were the only links missing.
 */
export function DropdownMenu({
  anchor = 'bottom',
  unmount = false,
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuItemsProps, 'as' | 'className'>) {
  return (
    <Headless.MenuItems
      {...props}
      transition
      anchor={anchor}
      unmount={unmount}
      className={clsx(
        className,
        '[--anchor-gap:--spacing(4)] [--anchor-padding:--spacing(1)] data-[anchor~=end]:[--anchor-offset:6px] data-[anchor~=start]:[--anchor-offset:-6px] sm:data-[anchor~=end]:[--anchor-offset:4px] sm:data-[anchor~=start]:[--anchor-offset:-4px]',
        'isolate z-50 w-max rounded-2xl p-2.5',
        'outline outline-transparent focus:outline-hidden',
        'overflow-y-auto',
        'bg-white/75 backdrop-blur-xl dark:bg-mist-800/75',
        'shadow-lg ring-1 ring-mist-950/10 dark:ring-white/10 dark:ring-inset',
        'supports-[grid-template-columns:subgrid]:grid supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]',
        'transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0',
      )}
    data-sentry-element="Headless.MenuItems" data-sentry-component="DropdownMenu" data-sentry-source-file="dropdown.tsx" />
  )
}

export function DropdownItem({
  className,
  ...props
}: { className?: string } & (
  | ({ href?: never } & Omit<Headless.MenuItemProps<'button'>, 'as' | 'className'>)
  | ({ href: string } & Omit<Headless.MenuItemProps<typeof Link>, 'as' | 'className'>)
)) {
  const classes = clsx(
    className,
    'group cursor-pointer rounded-2xl px-4 py-3 focus:outline-hidden sm:px-3.5 sm:py-2.5',
    'text-left text-base/6 text-mist-950 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]',
    'data-focus:bg-mist-950/5 data-focus:text-mist-950 dark:data-focus:bg-white/10 dark:data-focus:text-white',
    'data-disabled:opacity-50',
    'forced-color-adjust-none forced-colors:data-focus:bg-[Highlight] forced-colors:data-focus:text-[HighlightText] forced-colors:data-focus:*:data-[slot=icon]:text-[HighlightText]',
    'col-span-full grid grid-cols-[auto_1fr_1.5rem_0.5rem_auto] items-center supports-[grid-template-columns:subgrid]:grid-cols-subgrid',
    '*:data-[slot=icon]:col-start-1 *:data-[slot=icon]:row-start-1 *:data-[slot=icon]:mr-2.5 *:data-[slot=icon]:-ml-0.5 *:data-[slot=icon]:size-5 sm:*:data-[slot=icon]:mr-2 sm:*:data-[slot=icon]:size-4',
    '*:data-[slot=icon]:text-mist-500 data-focus:*:data-[slot=icon]:text-mist-600 dark:*:data-[slot=icon]:text-mist-400 dark:data-focus:*:data-[slot=icon]:text-white',
    '*:data-[slot=avatar]:mr-2.5 *:data-[slot=avatar]:-ml-1 *:data-[slot=avatar]:size-6 sm:*:data-[slot=avatar]:mr-2 sm:*:data-[slot=avatar]:size-5',
  )

  return typeof props.href === 'string' ? (
    <Headless.MenuItem as={Link} unstyled {...props} className={classes} data-sentry-element="Headless.MenuItem" data-sentry-component="DropdownItem" data-sentry-source-file="dropdown.tsx" />
  ) : (
    <Headless.MenuItem as="button" type="button" {...props} className={classes} data-sentry-element="Headless.MenuItem" data-sentry-component="DropdownItem" data-sentry-source-file="dropdown.tsx" />
  )
}

export function DropdownDivider({
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuSeparatorProps, 'as' | 'className'>) {
  return (
    <Headless.MenuSeparator
      {...props}
      className={clsx(
        className,
        'col-span-full mx-3.5 my-1 h-px border-0 bg-mist-950/5 sm:mx-3 dark:bg-white/10 forced-colors:bg-[CanvasText]',
      )}
    data-sentry-element="Headless.MenuSeparator" data-sentry-component="DropdownDivider" data-sentry-source-file="dropdown.tsx" />
  )
}

export function DropdownLabel({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div {...props} data-slot="label" className={clsx(className, 'col-start-2 row-start-1')} data-sentry-component="DropdownLabel" data-sentry-source-file="dropdown.tsx" />
}

export function DropdownDescription({
  className,
  ...props
}: { className?: string } & Omit<Headless.DescriptionProps, 'as' | 'className'>) {
  return (
    <Headless.Description
      data-slot="description"
      {...props}
      className={clsx(
        className,
        'col-span-2 col-start-2 row-start-2 text-sm/5 text-mist-500 group-data-focus:text-mist-700 sm:text-xs/5 dark:text-mist-400 dark:group-data-focus:text-white forced-colors:group-data-focus:text-[HighlightText]',
      )}
    data-sentry-element="Headless.Description" data-sentry-component="DropdownDescription" data-sentry-source-file="dropdown.tsx" />
  )
}

export function DropdownShortcut({
  keys,
  className,
  ...props
}: { keys: string | string[]; className?: string } & Omit<Headless.DescriptionProps<'kbd'>, 'as' | 'className'>) {
  return (
    <Headless.Description
      as="kbd"
      {...props}
      className={clsx(className, 'col-start-5 row-start-1 flex justify-self-end')}
    data-sentry-element="Headless.Description" data-sentry-component="DropdownShortcut" data-sentry-source-file="dropdown.tsx">
      {(Array.isArray(keys) ? keys : keys.split('')).map((char, index) => (
        <kbd
          key={index}
          className={clsx([
            'min-w-[2ch] text-center font-sans text-mist-400 capitalize group-data-focus:text-white forced-colors:group-data-focus:text-[HighlightText]',
            index > 0 && char.length > 1 && 'pl-1',
          ])}
        >
          {char}
        </kbd>
      ))}
    </Headless.Description>
  )
}
